import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { qualityService } from '../services/quality.service';
import { message } from 'antd';

// =====================================================
// DEPARTMENT KPI HOOKS
// =====================================================

export const useDepartmentKPIs = (filters?: { department_id?: string; is_active?: boolean }) => {
  return useQuery({
    queryKey: ['department-kpis', filters],
    queryFn: () => qualityService.getDepartmentKPIs(filters),
  });
};

export const useDepartmentKPI = (id: string) => {
  return useQuery({
    queryKey: ['department-kpi', id],
    queryFn: () => qualityService.getDepartmentKPIById(id),
    enabled: !!id,
  });
};

export const useCreateDepartmentKPI = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => qualityService.createDepartmentKPI(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['department-kpis'] });
      message.success('Department KPI created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create Department KPI');
    },
  });
};

export const useUpdateDepartmentKPI = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      qualityService.updateDepartmentKPI(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['department-kpis'] });
      queryClient.invalidateQueries({ queryKey: ['department-kpi', variables.id] });
      message.success('Department KPI updated successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update Department KPI');
    },
  });
};

export const useDeleteDepartmentKPI = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => qualityService.deleteDepartmentKPI(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['department-kpis'] });
      message.success('Department KPI deleted successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete Department KPI');
    },
  });
};

// =====================================================
// QUALITY PLAN KPI HOOKS
// =====================================================

export const useQualityPlanKPIs = (qualityPlanId: string) => {
  return useQuery({
    queryKey: ['quality-plan-kpis', qualityPlanId],
    queryFn: () => qualityService.getQualityPlanKPIs(qualityPlanId),
    enabled: !!qualityPlanId,
  });
};

export const useAssignKPIToQualityPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => qualityService.assignKPIToQualityPlan(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quality-plan-kpis', variables.quality_plan_id] });
      message.success('KPI assigned to quality plan successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to assign KPI to quality plan');
    },
  });
};

export const useUpdateQualityPlanKPI = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      qualityService.updateQualityPlanKPI(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quality-plan-kpis'] });
      message.success('Quality Plan KPI updated successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update Quality Plan KPI');
    },
  });
};

export const useRemoveKPIFromQualityPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => qualityService.removeKPIFromQualityPlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quality-plan-kpis'] });
      message.success('KPI removed from quality plan successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to remove KPI from quality plan');
    },
  });
};

// =====================================================
// KPI MEASUREMENT HOOKS
// =====================================================

export const useKPIMeasurements = (
  qualityPlanKPIId: string,
  filters?: { from_date?: string; to_date?: string }
) => {
  return useQuery({
    queryKey: ['kpi-measurements', qualityPlanKPIId, filters],
    queryFn: () => qualityService.getKPIMeasurements(qualityPlanKPIId, filters),
    enabled: !!qualityPlanKPIId,
  });
};

export const useRecordKPIMeasurement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => qualityService.recordKPIMeasurement(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['kpi-measurements', variables.quality_plan_kpi_id] });
      queryClient.invalidateQueries({ queryKey: ['kpi-summary'] });
      queryClient.invalidateQueries({ queryKey: ['kpi-performance'] });
      message.success('KPI measurement recorded successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to record KPI measurement');
    },
  });
};

export const useUpdateKPIMeasurement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      qualityService.updateKPIMeasurement(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kpi-measurements'] });
      queryClient.invalidateQueries({ queryKey: ['kpi-summary'] });
      message.success('KPI measurement updated successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update KPI measurement');
    },
  });
};

export const useVerifyKPIMeasurement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, verifiedBy, notes }: { id: string; verifiedBy: string; notes?: string }) => 
      qualityService.verifyKPIMeasurement(id, verifiedBy, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kpi-measurements'] });
      message.success('KPI measurement verified successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to verify KPI measurement');
    },
  });
};

// =====================================================
// KPI ANALYTICS & REPORTING HOOKS
// =====================================================

export const useKPISummaryByDepartment = (departmentId: string, period?: string) => {
  return useQuery({
    queryKey: ['kpi-summary', departmentId, period],
    queryFn: () => qualityService.getKPISummaryByDepartment(departmentId, period),
    enabled: !!departmentId,
  });
};

export const useKPITrend = (
  qualityPlanKPIId: string,
  fromDate?: string,
  toDate?: string
) => {
  return useQuery({
    queryKey: ['kpi-trend', qualityPlanKPIId, fromDate, toDate],
    queryFn: () => qualityService.getKPITrend(qualityPlanKPIId, fromDate, toDate),
    enabled: !!qualityPlanKPIId,
  });
};

export const useOverallKPIPerformance = (filters?: { company_id?: string; period?: string }) => {
  return useQuery({
    queryKey: ['kpi-performance', filters],
    queryFn: () => qualityService.getOverallKPIPerformance(filters),
  });
};

export default {
  // Department KPI
  useDepartmentKPIs,
  useDepartmentKPI,
  useCreateDepartmentKPI,
  useUpdateDepartmentKPI,
  useDeleteDepartmentKPI,

  // Quality Plan KPI
  useQualityPlanKPIs,
  useAssignKPIToQualityPlan,
  useUpdateQualityPlanKPI,
  useRemoveKPIFromQualityPlan,

  // KPI Measurements
  useKPIMeasurements,
  useRecordKPIMeasurement,
  useUpdateKPIMeasurement,
  useVerifyKPIMeasurement,

  // Analytics
  useKPISummaryByDepartment,
  useKPITrend,
  useOverallKPIPerformance,
};

